import API_BASE_URL from '../config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const ExpenseManager = () => {
    const navigate = useNavigate();
    const [allOrders, setAllOrders] = useState([]); // Store all fetched data
    const [filteredOrders, setFilteredOrders] = useState([]); // Data after search/toggle filter
    const [selectedOrderIds, setSelectedOrderIds] = useState([]);
    const [bulkAmount, setBulkAmount] = useState("");
    const [expenseType, setExpenseType] = useState("to_karigar");
    const [loading, setLoading] = useState(false);
    
    // New States for Search and Toggle
    const [searchTerm, setSearchTerm] = useState("");
    const [showDelivered, setShowDelivered] = useState(false);

    useEffect(() => {
        fetchActiveOrders();
    }, []);

    // Filter logic whenever dependencies change
    useEffect(() => {
        let updatedList = [...allOrders];

        // 1. Filter by Delivery Status if switch is OFF
        if (!showDelivered) {
            updatedList = updatedList.filter(o => o.odrsts !== "Delivered");
        }

        // 2. Filter by Search Term (Customer Name or Article Name)
        if (searchTerm.trim() !== "") {
            const term = searchTerm.toLowerCase();
            updatedList = updatedList.filter(o => 
                o.custname?.toLowerCase().includes(term) || 
                o.articlename?.toLowerCase().includes(term)
            );
        }

        setFilteredOrders(updatedList);
    }, [searchTerm, showDelivered, allOrders]);

    const fetchActiveOrders = async () => {
        try {
            setLoading(true);
            let result = await fetch(`${API_BASE_URL}/ordershowa`);
            let data = await result.json();
            setAllOrders(data);
        } catch (error) {
            console.error("Error fetching orders:", error);
        } finally {
            setLoading(false);
        }
    };

    const getExistingCost = (order) => {
        if (!order || !order.shipping_details) return 0;
        switch (expenseType) {
            case "to_karigar": return order.shipping_details.to_karigar || 0;
            case "to_sufna": return order.shipping_details.to_sufna || 0;
            case "to_courier": return order.shipping_details.to_courier || 0;
            case "to_customer": return order.shipping_details.to_customer || 0;
            default: return 0;
        }
    };

    const toggleOrderSelection = (id) => {
        setSelectedOrderIds(prev => 
            prev.includes(id) ? prev.filter(oid => oid !== id) : [...prev, id]
        );
    };

    const handleSelectAll = () => {
        if (selectedOrderIds.length === filteredOrders.length) {
            setSelectedOrderIds([]); 
        } else {
            setSelectedOrderIds(filteredOrders.map(o => o._id));
        }
    };

    const handleBatchUpdate = async () => {
        if (!bulkAmount || Number(bulkAmount) <= 0 || selectedOrderIds.length === 0) {
            alert("Please enter a valid amount and select at least one order.");
            return;
        }

        const dividedAmount = (Number(bulkAmount) / selectedOrderIds.length).toFixed(2);
        const confirmMsg = `Applying ₹${dividedAmount} as "${expenseType.replace('_', ' ').toUpperCase()}" to ${selectedOrderIds.length} orders. Proceed?`;
        
        if (!window.confirm(confirmMsg)) return;

        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/batch-update-shipping`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    orderIds: selectedOrderIds,
                    amount: Number(dividedAmount),
                    type: expenseType 
                })
            });

            if (response.ok) {
                alert("✅ Bulk Expenses Applied Successfully!");
                navigate('/orders');
            } else {
                const errData = await response.json();
                alert(`❌ Failed: ${errData.message || "Server Error"}`);
            }
        } catch (error) {
            console.error("Batch error:", error);
            alert("❌ Server Error connecting to API");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container py-5 bg-light min-vh-100">
            <div className="row justify-content-center">
                <div className="col-lg-8">
                    <div className="d-flex align-items-center mb-4">
                        <button type="button" className="btn btn-white shadow-sm me-3 rounded-circle border" onClick={() => navigate('/orders')}>←</button>
                        <h2 className="fw-bold mb-0">Batch <span className="text-primary">Expense Manager</span></h2>
                    </div>

                    <div className="card shadow-sm border-0 p-4 mb-4">
                        <div className="row g-3 mb-4">
                            <div className="col-md-6">
                                <label className="form-label fw-bold text-secondary small text-uppercase">Total Bill Amount (₹)</label>
                                <input 
                                    type="number" 
                                    className="form-control form-control-lg border-primary fw-bold" 
                                    placeholder="0.00" 
                                    value={bulkAmount}
                                    onChange={(e) => setBulkAmount(e.target.value)}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-bold text-secondary small text-uppercase">Expense Category</label>
                                <select className="form-select form-select-lg fw-bold text-primary" value={expenseType} onChange={(e) => setExpenseType(e.target.value)}>
                                    <option value="to_karigar">To Karigar</option>
                                    <option value="to_sufna">To Sufna</option>
                                    <option value="to_courier">To Courier</option>
                                    <option value="to_customer">To Customer</option>
                                </select>
                            </div>
                        </div>

                        <div className={`alert ${selectedOrderIds.length > 0 ? 'alert-primary' : 'alert-light border'} py-3 mb-0`}>
                            {selectedOrderIds.length > 0 ? (
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Division: <strong>₹{bulkAmount || 0}</strong> ÷ <strong>{selectedOrderIds.length}</strong> suits</span>
                                    <span className="fs-5 fw-bold text-primary">₹{(Number(bulkAmount) / selectedOrderIds.length || 0).toFixed(2)} <small className="fs-6 fw-normal text-dark">/each</small></span>
                                </div>
                            ) : (
                                <div className="text-center text-muted">Select orders from the list below to calculate division.</div>
                            )}
                        </div>
                    </div>

                    {/* New Search and Toggle Controls */}
                    <div className="card shadow-sm border-0 p-3 mb-3 bg-white">
                        <div className="row align-items-center g-3">
                            <div className="col-md-7">
                                <div className="input-group">
                                    <span className="input-group-text bg-transparent border-end-0 text-muted">
                                        <i className="bi bi-search small">🔍</i>
                                    </span>
                                    <input 
                                        type="text" 
                                        className="form-control border-start-0 ps-0" 
                                        placeholder="Search by customer or article..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="col-md-5 d-flex justify-content-md-end">
                                <div className="form-check form-switch">
                                    <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        role="switch" 
                                        id="deliveredSwitch"
                                        checked={showDelivered}
                                        onChange={(e) => setShowDelivered(e.target.checked)}
                                    />
                                    <label className="form-check-label small fw-bold text-secondary" htmlFor="deliveredSwitch">
                                        SHOW DELIVERED
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="card shadow-sm border-0 p-0 overflow-hidden">
                        <div className="bg-white p-3 border-bottom d-flex justify-content-between align-items-center">
                            <h5 className="fw-bold mb-0">Listings ({filteredOrders.length})</h5>
                            <button 
                                className="btn btn-sm btn-outline-primary fw-bold"
                                onClick={handleSelectAll}
                                disabled={filteredOrders.length === 0}
                            >
                                {selectedOrderIds.length > 0 && selectedOrderIds.length === filteredOrders.length ? "Deselect All" : "Select All"}
                            </button>
                        </div>
                        
                        <div className="list-group list-group-flush overflow-auto" style={{maxHeight: '400px'}}>
                            {filteredOrders.length > 0 ? filteredOrders.map(order => {
                                const existing = getExistingCost(order);
                                const isSelected = selectedOrderIds.includes(order._id);
                                
                                return (
                                    <label key={order._id} className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center py-3 ${isSelected ? 'bg-light-primary' : ''}`} style={{cursor: 'pointer'}}>
                                        <div className="d-flex align-items-center">
                                            <input 
                                                className="form-check-input me-3" 
                                                type="checkbox" 
                                                style={{transform: 'scale(1.2)'}}
                                                checked={isSelected}
                                                onChange={() => toggleOrderSelection(order._id)}
                                            />
                                            <div>
                                                <div className="fw-bold text-dark">
                                                    {order.custname} 
                                                    {order.odrsts === "Delivered" && <span className="badge bg-success ms-2 small" style={{fontSize: '0.6rem'}}>DELIVERED</span>}
                                                </div>
                                                <div className="text-muted small">{order.articlename}</div>
                                            </div>
                                        </div>
                                        <div className="text-end">
                                            <div className="small text-uppercase text-muted" style={{fontSize: '0.65rem'}}>Existing Saved</div>
                                            <div className={`fw-bold ${existing > 0 ? 'text-danger' : 'text-muted'}`}>₹{existing}</div>
                                        </div>
                                    </label>
                                );
                            }) : (
                                <div className="p-5 text-center text-muted">
                                    {loading ? "Loading orders..." : "No orders found matching your criteria."}
                                </div>
                            )}
                        </div>

                        <div className="p-4 bg-white border-top">
                            <button 
                                className="btn btn-primary btn-lg w-100 fw-bold shadow" 
                                onClick={handleBatchUpdate}
                                disabled={loading || selectedOrderIds.length === 0}
                            >
                                {loading ? (
                                    <span><span className="spinner-border spinner-border-sm me-2"></span>Processing...</span>
                                ) : (
                                    `Apply Expense to ${selectedOrderIds.length} Orders`
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <style>{`
                .bg-light-primary { background-color: #f0f7ff !important; }
                .form-switch .form-check-input { width: 2.5em; height: 1.25em; cursor: pointer; }
            `}</style>
        </div>
    );
};

export default ExpenseManager;
